import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

/**
 * Jeda polling saat channel realtime sedang PUTUS.
 *
 * Dipakai layar sebagai pengganti jeda normalnya selama `connected === false`.
 * 4 detik: cukup rapat supaya layar tidak terasa mati, masih jauh lebih longgar
 * daripada memuat ulang terus-menerus. Ini keadaan darurat sementara, bukan
 * ritme normal — begitu channel tersambung lagi, layar kembali ke jeda normal.
 */
export const REALTIME_DOWN_POLL_MS = 4000;

// Penyambungan ulang: 1s, 2s, 4s, 8s, 16s, lalu mentok 30s.
//
// Bertambah, bukan tetap: kalau Realtime sedang mati atau jaringan putus,
// mencoba tiap detik selamanya hanya membebani klien dan server tanpa
// mempercepat apa pun. Ada batas atas supaya percobaan tidak melar jadi
// hitungan jam — layar yang menyerah diam-diam persis cacat yang REALTIME-01
// biarkan hidup tujuh minggu.
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

/**
 * Polling yang berhenti saat komponen dilepas DAN saat tab tidak terlihat.
 *
 * Kenapa ikut berhenti saat tab tersembunyi. Polling di tab latar adalah
 * permintaan yang hasilnya tidak dilihat siapa pun — ia hanya membebani
 * backend, dan dengan banyak tab terbuka bebannya berlipat. Saat tab kembali
 * terlihat, data langsung diambil SEKALI di luar jadwal, karena yang ditunggu
 * pengguna saat itu adalah keadaan sekarang, bukan keadaan saat ia pergi.
 *
 * Params:
 *   - fn (function)      — pengambil data; selalu versi terbaru lewat ref
 *   - intervalMs (number)— jeda; 0 / null mematikan polling
 *   - opts: { enabled?: boolean }
 */
export function useVisiblePolling(fn, intervalMs, opts = {}) {
  const { enabled = true } = opts;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    let timer = null;
    const terlihat = () =>
      typeof document === "undefined" || document.visibilityState === "visible";

    const mulai = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (terlihat()) fnRef.current?.();
      }, intervalMs);
    };
    const henti = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVisibilityChange = () => {
      if (terlihat()) {
        fnRef.current?.();
        mulai();
      } else {
        henti();
      }
    };

    if (terlihat()) mulai();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      henti();
    };
  }, [intervalMs, enabled]);
}

/**
 * Langganan realtime Supabase untuk SATU tabel.
 *
 * MENGEMBALIKAN `connected` (boolean) — true hanya sesudah status SUBSCRIBED
 * benar-benar diterima. Layar memakainya untuk mempercepat polling selama
 * channel putus.
 *
 * Kenapa nilai balik itu ada. Sebelumnya hook ini menelan CHANNEL_ERROR dan
 * TIMED_OUT jadi satu baris `console.warn`, lalu diam selamanya: channel yang
 * gagal tidak pernah dicoba lagi, dan layar tidak punya cara mengetahui bahwa
 * jalur utamanya sudah mati. Itu bentuk kegagalan yang sama dengan REALTIME-01
 * sendiri — sesuatu yang berhenti bekerja tanpa satu pun tanda di layar.
 *
 * Catatan lama yang MASIH berlaku: kalau tabel belum terdaftar di publication
 * `supabase_realtime`, subscribe tetap SUKSES (status SUBSCRIBED) tapi nol
 * event datang. `connected === true` karena itu berarti "saluran terbuka",
 * BUKAN "event pasti mengalir". Polling tetap dipasang sebagai jaring pengaman
 * (D108) justru karena kedua keadaan itu tidak bisa dibedakan dari sisi klien.
 *
 * Params:
 *   - table (string, wajib)
 *   - onChange (function, wajib) — dipanggil pada tiap event yang cocok
 *   - opts: { events?: ["INSERT","UPDATE","DELETE"]|"*", filter?: string, enabled?: boolean }
 */
export function useRealtimeTable(table, onChange, opts = {}) {
  const { events = "*", filter, enabled = true } = opts;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled || !table) {
      setConnected(false);
      return undefined;
    }

    // `dibuang` menjaga semua callback asinkron: tanpa itu, status yang datang
    // sesudah komponen dilepas akan memanggil setState pada komponen mati dan
    // menjadwalkan penyambungan ulang untuk channel yang sudah tidak dipakai.
    let dibuang = false;
    let chan = null;
    let timer = null;
    let percobaan = 0;

    const jadwalkanUlang = () => {
      if (dibuang || timer) return;
      const jeda = Math.min(RECONNECT_BASE_MS * 2 ** percobaan, RECONNECT_MAX_MS);
      // Jitter kecil supaya banyak tab yang putus bersamaan tidak menyerbu
      // server serentak saat jaringan pulih.
      const jitter = Math.floor(Math.random() * 250);
      percobaan += 1;
      timer = setTimeout(() => {
        timer = null;
        if (dibuang) return;
        if (chan) {
          supabase.removeChannel(chan);
          chan = null;
        }
        pasang();
      }, jeda + jitter);
    };

    const pasang = () => {
      if (dibuang) return;
      const channelName = `rt-${table}-${Math.random().toString(36).slice(2, 8)}`;
      const ev =
        events === "*" || (Array.isArray(events) && events.length === 0) ? "*" : events;
      chan = supabase.channel(channelName);

      const subscribeAll = (event) => {
        const cfg = { event, schema: "public", table };
        if (filter) cfg.filter = filter;
        chan.on("postgres_changes", cfg, (payload) => {
          try {
            onChangeRef.current?.(payload);
          } catch (e) {
            // Galat pelanggan tidak boleh merobohkan channel.
            // eslint-disable-next-line no-console
            console.error("[useRealtimeTable] handler error", e);
          }
        });
      };

      if (ev === "*") {
        subscribeAll("*");
      } else {
        (Array.isArray(ev) ? ev : [ev]).forEach((e) => subscribeAll(e));
      }

      chan.subscribe((status) => {
        if (dibuang) return;
        if (status === "SUBSCRIBED") {
          percobaan = 0;
          setConnected(true);
          return;
        }
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
          setConnected(false);
          // eslint-disable-next-line no-console
          console.warn(
            `[useRealtimeTable] channel ${channelName} status=${status} — menyambung ulang, polling dipercepat sementara`
          );
          jadwalkanUlang();
        }
      });
    };

    pasang();

    return () => {
      dibuang = true;
      if (timer) clearTimeout(timer);
      if (chan) supabase.removeChannel(chan);
    };
  }, [table, events, filter, enabled]);

  return connected;
}

export default useRealtimeTable;
